import React, { useState } from 'react';
import { Shield, Key, Smartphone, History, Save } from 'lucide-react';
import { supabase } from '../lib/supabase';

export function SecuritySettings() {
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [isTwoFactorEnabled, setIsTwoFactorEnabled] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) throw error;

      setSuccess('Password updated successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setIsChangingPassword(false);
    } catch (error) {
      setError(error.message);
    }
  };

  return (
    <div className="max-w-2xl mx-auto p-6">
      <div className="space-y-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-100 flex items-center gap-2">
            <Shield className="w-5 h-5 text-indigo-500" />
            Security Settings
          </h2>
          <p className="text-sm text-gray-400 mt-1">
            Manage your account security preferences
          </p>
        </div>

        {error && (
          <div className="p-3 bg-red-500/10 text-red-400 rounded-lg text-sm">
            {error}
          </div>
        )}

        {success && (
          <div className="p-3 bg-green-500/10 text-green-400 rounded-lg text-sm">
            {success}
          </div>
        )}

        <div className="space-y-4">
          {/* Password Section */}
          <div className="bg-gray-800 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Key className="w-5 h-5 text-gray-400" />
                <h3 className="text-lg font-medium text-gray-200">Password</h3>
              </div>
              <button
                onClick={() => setIsChangingPassword(!isChangingPassword)}
                className="text-sm text-indigo-400 hover:text-indigo-300"
              >
                {isChangingPassword ? 'Cancel' : 'Change'}
              </button>
            </div>

            {isChangingPassword && (
              <form onSubmit={handleChangePassword} className="mt-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300">
                    Current Password
                  </label>
                  <input
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className="mt-1 block w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-gray-100"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300">
                    New Password
                  </label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="mt-1 block w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-gray-100"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300">
                    Confirm New Password
                  </label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="mt-1 block w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-gray-100"
                    required
                  />
                </div>
                <button
                  type="submit"
                  className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                >
                  <Save className="w-4 h-4" />
                  Update Password
                </button>
              </form>
            )}
          </div>

          {/* Two-Factor Authentication */}
          <div className="bg-gray-800 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Smartphone className="w-5 h-5 text-gray-400" />
                <h3 className="text-lg font-medium text-gray-200">
                  Two-Factor Authentication
                </h3>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={isTwoFactorEnabled}
                  onChange={() => setIsTwoFactorEnabled(!isTwoFactorEnabled)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
              </label>
            </div>
          </div>

          {/* Login History */}
          <div className="bg-gray-800 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-4">
              <History className="w-5 h-5 text-gray-400" />
              <h3 className="text-lg font-medium text-gray-200">Login History</h3>
            </div>
            <div className="space-y-2">
              {/* Example login history items */}
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Current session</span>
                <span className="text-gray-300">Now</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Last login</span>
                <span className="text-gray-300">2 days ago</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}